
import { supabase } from './client';

// Optimized Profiles API with proper method chaining support
export const profilesApi = {
  select: () => supabase.from('profiles').select(),
  insert: (data: any) => supabase.from('profiles').insert(data),
  update: (data: any) => supabase.from('profiles').update(data),
  // Add a query builder method for more complex queries
  query: () => supabase.from('profiles'),
  // Get a single profile by user ID (optimized)
  getById: (userId: string) => 
    supabase.from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle() // Use maybeSingle instead of single to avoid errors
};

// Promote user to admin role with improved error handling
export const promoteUserToAdmin = async (email: string) => {
  try {
    // First check if the user exists
    const { data: existingUser, error: lookupError } = await supabase
      .from('profiles')
      .select('id, email, role')
      .eq('email', email)
      .maybeSingle();
    
    if (lookupError) {
      console.error('Error looking up user:', lookupError);
      return { success: false, error: lookupError.message };
    }
    
    if (!existingUser) {
      return { success: false, error: `No user found with email ${email}` };
    }
    
    // If the user is already an admin, no need to update
    if (existingUser.role === 'admin') {
      return { success: true, data: existingUser, message: 'User is already an admin' };
    }
    
    // Update the user's role to admin
    const { data, error } = await supabase
      .from('profiles')
      .update({ role: 'admin' })
      .eq('email', email)
      .select('id, email, role')
      .single();

    if (error) {
      console.error('Error promoting user to admin:', error);
      return { success: false, error: error.message };
    }
    
    return { success: true, data, message: 'User successfully promoted to admin' };
  } catch (error: any) {
    console.error('Unexpected error promoting user to admin:', error);
    return { success: false, error: error.message || 'An unexpected error occurred' };
  }
};

// Demote admin to regular user
export const demoteAdminToUser = async (email: string) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .update({ role: 'user' })
      .eq('email', email)
      .select('id, email, role')
      .single();

    if (error) {
      console.error('Error demoting admin to user:', error);
      return { success: false, error: error.message };
    }
    
    return { success: true, data, message: 'Admin successfully demoted to user' };
  } catch (error: any) {
    console.error('Unexpected error demoting admin:', error);
    return { success: false, error: error.message || 'An unexpected error occurred' };
  }
};

// Get all users with a specific role
export const getUsersByRole = async (role: string) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, email, role, created_at')
      .eq('role', role);
      
    if (error) throw error;
    return { success: true, data };
  } catch (error: any) {
    console.error('Error fetching users by role:', error);
    return { success: false, error: error.message };
  }
};
